import type { ClientMessage } from "./types";

/**
 * WebSocket-Transport mit Offline-Queue und automatischem Reconnect (US1.5).
 * Nachrichten werden in einer In-Memory-Queue gehalten; bei Verbindungsverlust
 * wird gepuffert und nach Reconnect in Reihenfolge nachgesendet.
 */
export class Transport {
  private ws: WebSocket | undefined;
  private readonly url: string;
  private queue: string[] = [];
  private open = false;
  private closedByUser = false;
  private backoff = 500;
  private readonly maxBackoff = 10_000;
  private readonly maxQueue = 50_000;
  private readonly debug: boolean;
  private reconnectTimer: ReturnType<typeof setTimeout> | undefined;
  private readonly onMessageCb?: (msg: unknown) => void;

  constructor(url: string, debug = false, onMessage?: (msg: unknown) => void) {
    this.url = url;
    this.debug = debug;
    this.onMessageCb = onMessage;
  }

  connect(): void {
    this.closedByUser = false;
    this.openSocket();
  }

  private openSocket(): void {
    try {
      this.ws = new WebSocket(this.url);
    } catch (err) {
      this.log("WebSocket-Konstruktion fehlgeschlagen", err);
      this.scheduleReconnect();
      return;
    }

    this.ws.onopen = () => {
      this.open = true;
      this.backoff = 500;
      this.log("verbunden");
      this.flush();
    };

    this.ws.onclose = () => {
      this.open = false;
      if (!this.closedByUser) this.scheduleReconnect();
    };

    this.ws.onerror = () => {
      // onclose folgt; dort wird der Reconnect geplant.
      this.log("WebSocket-Fehler");
    };

    if (this.onMessageCb) {
      this.ws.onmessage = (evt) => {
        let msg: unknown;
        try {
          msg = JSON.parse(typeof evt.data === "string" ? evt.data : String(evt.data));
        } catch {
          return;
        }
        this.onMessageCb?.(msg);
      };
    }
  }

  private scheduleReconnect(): void {
    if (this.closedByUser || this.reconnectTimer) return;
    const delay = this.backoff;
    this.backoff = Math.min(this.backoff * 2, this.maxBackoff);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = undefined;
      this.openSocket();
    }, delay);
  }

  send(msg: ClientMessage): void {
    this.queue.push(JSON.stringify(msg));
    if (this.queue.length > this.maxQueue) {
      // Ältester Eintrag fällt raus, damit der Speicher nicht unbegrenzt wächst.
      this.queue.shift();
    }
    if (this.open) this.flush();
  }

  private flush(): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    while (this.queue.length > 0) {
      const item = this.queue[0]!;
      try {
        this.ws.send(item);
        this.queue.shift();
      } catch (err) {
        this.log("Senden fehlgeschlagen, behalte Queue", err);
        break;
      }
    }
  }

  close(): void {
    this.closedByUser = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = undefined;
    }
    this.flush();
    this.ws?.close();
    this.open = false;
  }

  private log(...args: unknown[]): void {
    if (this.debug) console.debug("[userlab:transport]", ...args);
  }
}
